import React from 'react';
import { motion } from 'framer-motion';
import { Users, MessageCircle, Sparkles } from 'lucide-react';
import { Card } from './ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';

const CommunitySection = () => {
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
    }
  };

  return (
    <section id="community" className="section-spacing">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-16"
        >
          <p className="label-uppercase mb-4">Join the Movement</p>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-normal mb-6">
            Join Our Global Community
          </h2>
          <p className="text-lg text-muted-foreground max-w-[65ch] mx-auto leading-relaxed">
            More than just a podcast.
          </p>
          <p className="text-lg text-muted-foreground max-w-[65ch] mx-auto leading-relaxed mt-2">
            Connect with fellow dreamers, share your journey, and shape future conversations.
          </p>
        </motion.div>

        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-16"
        >
          <motion.div variants={item}>
            <Card className="p-8 h-full shadow-warm hover-lift feature-card">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-6 feature-icon transition-all">
                <MessageCircle className="w-7 h-7 text-primary" />
              </div>
              <h3 className="text-2xl font-normal mb-3">Debrief Episodes</h3>
              <p className="text-muted-foreground leading-relaxed">
                Chat with April and fellow listeners about each episode's most powerful moments and lessons.
              </p>
            </Card>
          </motion.div>

          <motion.div variants={item}>
            <Card className="p-8 h-full shadow-warm hover-lift feature-card">
              <div className="w-14 h-14 rounded-2xl bg-secondary/20 flex items-center justify-center mb-6 feature-icon transition-all">
                <Users className="w-7 h-7 text-secondary" />
              </div>
              <h3 className="text-2xl font-normal mb-3">Share Your Story</h3>
              <p className="text-muted-foreground leading-relaxed">
                Connect with global nomads who understand the journey. Your experience matters here.
              </p>
            </Card>
          </motion.div>

          <motion.div variants={item}>
            <Card className="p-8 h-full shadow-warm hover-lift feature-card">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-6 feature-icon transition-all">
                <Sparkles className="w-7 h-7 text-primary" />
              </div>
              <h3 className="text-2xl font-normal mb-3">Shape the Show</h3>
              <p className="text-muted-foreground leading-relaxed">
                Vote on future guests and suggest topics. This community helps decide what stories get told.
              </p>
            </Card>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-2xl mx-auto"
        >
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="item-1" className="border-border/50">
              <AccordionTrigger className="text-left text-lg hover:text-primary">How does the WhatsApp group work?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed text-base">
                After you join, April will personally add you to our private WhatsApp community. We discuss episodes after they air, share travel tips, and vote on upcoming guests. It's informal, respectful, and global.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="border-border/50">
              <AccordionTrigger className="text-left text-lg hover:text-primary">Is my information private?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed text-base">
                Absolutely. Your email and WhatsApp are only used for community access. We never share your details with third parties or send spam. You can leave the group anytime.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="border-border/50">
              <AccordionTrigger className="text-left text-lg hover:text-primary">What if I'm not a nomad yet?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed text-base">
                Perfect! Many members are dreamers planning their move. The community welcomes anyone curious about building a life elsewhere, regardless of where you are in your journey.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
};

export default CommunitySection;